/** biome-ignore-all lint/a11y/useValidAnchor: need links here */
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import useToggle from '@rio-cloud/rio-uikit/useToggle';

import { NotificationList } from './NotificationList';
import { gaPush, TRACKING_CATEGORIES } from '../../configuration/setup/googleAnalytics';
import { AccessInformationDialog } from './AccessInformationDialog';
import { LINK_CUSTOM_RULE, LINK_VEHICLE_DOWNTIME_TEMPLATE, LINK_VEHICLE_POSITION_TEMPLATE } from './eventMonitorLinks';

export const EmptyVehicleNotificationList = () => {
    const [targetLink, setTargetLink] = useState(LINK_CUSTOM_RULE);
    const { isToggled: isRuleInfoDialogShown, toggle: toggleRuleInfoDialog } = useToggle();

    const handleNewRule = () => {
        gaPush({
            category: TRACKING_CATEGORIES.NAVIGATION,
            label: 'Empty notifications state',
            action: 'Clicked "Create rule now" button',
        });
        setTargetLink(LINK_CUSTOM_RULE);
        toggleRuleInfoDialog();
    };

    const handleVehicleDowntime = (event: React.MouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        gaPush({
            category: TRACKING_CATEGORIES.NAVIGATION,
            label: 'Empty notifications state',
            action: 'Clicked "Vehicle downtime template" link',
        });
        setTargetLink(LINK_VEHICLE_DOWNTIME_TEMPLATE);
        toggleRuleInfoDialog();
    };

    const handleVehiclePosition = () => {
        gaPush({
            category: TRACKING_CATEGORIES.NAVIGATION,
            label: 'Empty notifications state',
            action: 'Clicked "No vehicle position template" link',
        });
        setTargetLink(LINK_VEHICLE_POSITION_TEMPLATE);
        toggleRuleInfoDialog();
    };

    const handleCloseRuleDialog = () => {
        gaPush({
            category: TRACKING_CATEGORIES.NAVIGATION,
            label: 'Empty notifications state dialog',
            action: 'Closed new rule dialog',
        });
        toggleRuleInfoDialog();
    };

    const handleAcknowledgedRuleDialog = () => {
        gaPush({
            category: TRACKING_CATEGORIES.NAVIGATION,
            label: 'Empty notifications state dialog',
            action: `Opened rule editor via dialog with link ${targetLink}`,
        });
        toggleRuleInfoDialog();
    };

    return (
        <NotificationList listLabel='no-notifications'>
            <div className='margin-top-25 padding-x-15 text-center'>
                <div className='margin-bottom-15'>
                    <div className='text-size-h3 text-size-h2-xl line-height-125rel text-color-dark text-medium width-500 max-width-100pct text-wrap-balance'>
                        <FormattedMessage id='intl-msg:notifications.vehicleNotifications.emptyState' />
                    </div>
                    <div className='margin-top-15'>
                        <FormattedMessage
                            id='intl-msg:notifications.vehicleNotificationsUpdate'
                            values={{
                                b: chunk => <b>{chunk}</b>,
                            }}
                            defaultMessage={
                                '<b>We have revised our vehicle notifications.</b> You can now decide for yourself what you want to be ' +
                                'informed about. And about much more than just the <b>downtimes.</b>'
                            }
                        />
                    </div>
                </div>
                <a onClick={handleNewRule} className='btn btn-primary btn-icon-right' target='_blank' rel='noreferrer'>
                    <FormattedMessage id='intl-msg:notifications.createRuleButton' defaultMessage='Create rule now' />
                    <span className='rioglyph rioglyph-new-window' />
                </a>
                <div className='alert alert-info margin-top-50 text-left'>
                    <div className='display-flex gap-10'>
                        <span className='text-color-info text-size-h4 rioglyph rioglyph-info-sign' />
                        <div>
                            <FormattedMessage
                                id='intl-msg:notifications.downtimeTemplateHint'
                                defaultMessage={
                                    'Would you like to continue receiving similar notifications as before? Use one of our templates below ' +
                                    'to quickly set up these notifications with just a few clicks.'
                                }
                            />
                            <div className='margin-top-25'>
                                <div className='display-grid grid-cols-2 gap-10'>
                                    {/* Old exception: "Vehicle stands still for > 24 hours." */}
                                    <a
                                        onClick={handleVehicleDowntime}
                                        className='alert-link text-medium text-center display-flex flex-column align-items-center gap-10'
                                        target='_blank'
                                        rel='noreferrer'
                                    >
                                        <div className='display-flex'>
                                            <span className='rioglyph-icon-pair text-size-h1'>
                                                <span className='rioglyph rioglyph-truck' />
                                                <span className='rioglyph rioglyph-parking bg-warning text-color-white' />
                                            </span>
                                        </div>
                                        <div className='line-height-125rel'>
                                            <FormattedMessage
                                                id='intl-msg:notifications.downtimeTemplate'
                                                defaultMessage='Vehicle downtime template'
                                            />
                                        </div>
                                    </a>

                                    {/* Old exception: "No vehicle positions received for > 12 hours." */}
                                    <a
                                        onClick={handleVehiclePosition}
                                        className='alert-link text-medium text-center display-flex flex-column align-items-center gap-10'
                                        target='_blank'
                                        rel='noreferrer'
                                    >
                                        <div className='display-flex'>
                                            <span className='rioglyph-icon-pair text-size-h1'>
                                                <span className='rioglyph rioglyph-truck' />
                                                <span className='rioglyph rioglyph-map-marker bg-danger text-color-white' />
                                            </span>
                                        </div>
                                        <div className='line-height-125rel'>
                                            <FormattedMessage
                                                id='intl-msg:notifications.noPositionTemplate'
                                                defaultMessage='No vehicle position template'
                                            />
                                        </div>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <AccessInformationDialog
                show={isRuleInfoDialogShown}
                link={targetLink}
                onClose={handleCloseRuleDialog}
                onAccept={handleAcknowledgedRuleDialog}
            />
        </NotificationList>
    );
};
